// src/pages/clab-biodata-amended/components/AmendmentHistoryModal.jsx
import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const AmendmentHistoryModal = ({ data, onClose }) => {
  const [selectedCycle, setSelectedCycle] = useState(null);

  if (!data) return null;

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Get rejection info for a specific cycle
  const getRejectionForCycle = (cycleNumber) => {
    const cycleSummary = data.amendment_summary?.find(s => s.cycle === cycleNumber);
    return cycleSummary?.rejection || null;
  };

  // Get all rejection reasons for a specific cycle
  const getRejectionReasonsForCycle = (cycleNumber) => {
    const cycleSummary = data.amendment_summary?.find(s => s.cycle === cycleNumber);
    if (!cycleSummary?.rejection) return [];

    // If there's a rejection_reasons array, use it
    if (cycleSummary.rejection.rejection_reasons && Array.isArray(cycleSummary.rejection.rejection_reasons)) {
      return cycleSummary.rejection.rejection_reasons;
    }

    // Otherwise, fallback to single rejection reason
    if (cycleSummary.rejection.rejection_reason) {
      return [{ rejection_reason: cycleSummary.rejection.rejection_reason }];
    }

    return [];
  };

  const formatFieldName = (fieldName) => {
    return fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getChangeTypeIcon = (changeType) => {
    switch (changeType) {
      case 'field_update':
        return 'edit';
      case 'document_upload':
        return 'upload';
      case 'document_replace':
        return 'refresh';
      case 'document_delete':
        return 'trash';
      default:
        return 'edit';
    }
  };

  const getChangeTypeColor = (changeType) => {
    switch (changeType) {
      case 'field_update':
        return 'text-info-600 bg-info-100';
      case 'document_upload':
        return 'text-success-600 bg-success-100';
      case 'document_replace':
        return 'text-warning-600 bg-warning-100';
      case 'document_delete':
        return 'text-danger-600 bg-danger-100';
      default:
        return 'text-text-secondary bg-background';
    }
  };

  const renderAmendmentDetail = (amendment) => (
    <div key={`${amendment.amendment_cycle}-${amendment.field_name}-${amendment.amended_at}`} 
         className="border border-border rounded-lg p-4 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-full ${getChangeTypeColor(amendment.change_type)}`}>
            <Icon name={getChangeTypeIcon(amendment.change_type)} size={16} />
          </div>
          <div>
            <h4 className="font-medium text-text-primary">
              {formatFieldName(amendment.field_name)}
            </h4>
            <p className="text-sm text-text-secondary capitalize">
              {amendment.change_type.replace(/_/g, ' ')}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-text-primary">
            {amendment.amendedBy?.full_name || 'Unknown User'}
          </p>
          <p className="text-xs text-text-tertiary">
            {formatDateTime(amendment.amended_at)}
          </p>
        </div>
      </div>

      {amendment.change_type === 'field_update' && (
        <div className="bg-background p-3 rounded border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-text-tertiary mb-1">Previous Value</label>
              <div className="p-2 bg-danger-50 border border-danger-200 rounded text-sm">
                <span className="text-danger-800">
                  {amendment.old_value || <em className="text-text-tertiary">Empty</em>}
                </span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-tertiary mb-1">New Value</label>
              <div className="p-2 bg-success-50 border border-success-200 rounded text-sm">
                <span className="text-success-800">
                  {amendment.new_value || <em className="text-text-tertiary">Empty</em>}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {amendment.change_type.includes('document') && amendment.metadata && (
        <div className="bg-background p-3 rounded border">
          <label className="block text-xs font-medium text-text-tertiary mb-1">Document Information</label>
          <div className="text-sm text-text-primary">
            {amendment.metadata.document_name && (
              <p>Document: {amendment.metadata.document_name}</p>
            )}
            {amendment.metadata.file_size && (
              <p>Size: {(amendment.metadata.file_size / 1024 / 1024).toFixed(2)} MB</p>
            )}
            {amendment.metadata.mime_type && (
              <p>Type: {amendment.metadata.mime_type}</p>
            )}
          </div>
        </div>
      )}

      {amendment.amendment_reason && (
        <div className="mt-3">
          <label className="block text-xs font-medium text-text-tertiary mb-1">Amendment Reason</label>
          <p className="text-sm text-text-primary bg-info-50 border border-info-200 rounded p-2">
            {amendment.amendment_reason}
          </p>
        </div>
      )}

    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-surface rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-text-primary">Amendment History</h2>
            <p className="text-text-secondary">
              {data.biodata?.full_name} - {data.total_cycles} amendment cycle(s)
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background rounded-md transition-colors"
          >
            <Icon name="x" size={24} />
          </button>
        </div>

        {/* Summary Cards */}
        <div className="p-6 border-b border-border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-primary-800">{data.total_cycles}</p>
              <p className="text-sm text-primary-600">Total Cycles</p>
            </div>
            <div className="bg-info-50 border border-info-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-info-800">
                {data.amendment_history?.filter(a => a.change_type === 'field_update').length || 0}
              </p>
              <p className="text-sm text-info-600">Field Changes</p>
            </div>
            <div className="bg-success-50 border border-success-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-success-800">
                {data.amendment_history?.filter(a => a.change_type.includes('document')).length || 0}
              </p>
              <p className="text-sm text-success-600">Document Changes</p>
            </div>
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-warning-800">
                {data.amendment_history?.length || 0}
              </p>
              <p className="text-sm text-warning-600">Total Changes</p>
            </div>
          </div>
        </div>

        {/* Cycle Selector */}
        {data.amendment_summary && data.amendment_summary.length > 1 && (
          <div className="p-6 border-b border-border">
            <label className="block text-sm font-medium text-text-secondary mb-2">Filter by Amendment Cycle</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCycle(null)}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  selectedCycle === null
                    ? 'bg-primary text-white border-primary'
                    : 'bg-surface text-text-secondary border-border hover:bg-background'
                }`}
              >
                All Cycles
              </button>
              {data.amendment_summary.map((cycle) => (
                <button
                  key={cycle.cycle}
                  onClick={() => setSelectedCycle(cycle.cycle)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    selectedCycle === cycle.cycle
                      ? 'bg-primary text-white border-primary'
                      : 'bg-surface text-text-secondary border-border hover:bg-background'
                  }`}
                >
                  Cycle {cycle.cycle} ({cycle.total_changes})
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Amendment Details */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {data.amendment_history && data.amendment_history.length > 0 ? (
            <div>
              {(() => {
                const filteredAmendments = data.amendment_history.filter(amendment =>
                  selectedCycle === null || amendment.amendment_cycle === selectedCycle
                );

                // Group amendments by cycle
                const amendmentsByCycle = filteredAmendments.reduce((acc, amendment) => {
                  const cycle = amendment.amendment_cycle;
                  if (!acc[cycle]) acc[cycle] = [];
                  acc[cycle].push(amendment);
                  return acc;
                }, {});

                // Render each cycle with its rejection info
                return Object.keys(amendmentsByCycle).sort((a, b) => b - a).map(cycle => {
                  const cycleNumber = parseInt(cycle);
                  const rejection = getRejectionForCycle(cycleNumber);
                  const rejectionReasons = getRejectionReasonsForCycle(cycleNumber);
                  const amendments = amendmentsByCycle[cycle];

                  return (
                    <div key={cycle} className="mb-8">
                      {/* Cycle Header with Rejection Info */}
                      <div className="bg-primary-50 border-l-4 border-primary-600 p-4 mb-4 rounded">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-primary-800 mb-2">
                              Amendment Cycle {cycle}
                            </h3>
                            {rejection && rejectionReasons.length > 0 && (
                              <div className="space-y-2">
                                <div className="bg-error-50 border border-error-200 rounded p-3">
                                  <label className="block text-xs font-medium text-error-800 mb-2">
                                    Why this amendment was needed:
                                  </label>
                                  <div className="flex flex-wrap gap-2">
                                    {rejectionReasons.map((reason, idx) => (
                                      <span
                                        key={idx}
                                        className="inline-flex items-center px-3 py-1 bg-error-100 text-error-800 border border-error-200 rounded-full text-sm font-medium"
                                        title={reason.rejection_reason}
                                      >
                                        {reason.rejection_reason || 'No reason'}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                {rejection.remarks && (
                                  <div className="bg-warning-50 border border-warning-200 rounded p-3">
                                    <label className="block text-xs font-medium text-warning-800 mb-1">
                                      Additional Details:
                                    </label>
                                    <p className="text-sm text-warning-700">
                                      {rejection.remarks}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="ml-4 bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                            {amendments.length} change{amendments.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>

                      {/* Amendments for this cycle */}
                      <div className="ml-4">
                        {amendments.map((amendment) => renderAmendmentDetail(amendment))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div className="text-center py-8">
              <Icon name="info" size={48} className="text-text-tertiary mx-auto mb-4" />
              <h3 className="text-lg font-medium text-text-secondary mb-2">No Amendment History</h3>
              <p className="text-text-tertiary">
                No detailed amendment history is available for this biodata.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-border bg-background">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AmendmentHistoryModal; 
